import { useTranslation } from 'react-i18next';
import { Row } from '@tanstack/react-table';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import OpenInquiryButton from '@/components/Inquiries/OpenInquiryButton';
import { formatDateShort } from '@/helpers/dateHelper';
import { formatCurrencyShort } from '@/helpers/currencyHelper';
import {
  CardAction,
  CardAmount,
  CardBody,
  CardIcon,
  CardMeta,
  CardRowActions,
  CardSide,
  CardStatusLine,
  CardTitle,
  ListCard,
} from '@/components/ui/CardList/CardList';
import bankIcon from '@/assets/icons/bank.svg';
import paymentIcon from '@/assets/icons/payment.svg';
import { isAptOrder } from './aptStandingOrders';
import {
  ActiveStatus,
  LastChargeStatus,
  activeStatusKey,
  STANDING_ORDER_TYPE_BANK,
  STANDING_ORDER_TYPE_CREDIT,
  getActiveStatus,
  getLastChargeStatus,
  getPaymentsPaid,
  parseDate,
} from './standingOrderModel';

/*
 * ============================================================
 *  כרטיס הוראת קבע — תצוגת המסך הצר (HAT-1170, HAT-1238)
 * ============================================================
 *
 * שלד שטוח כמו כרטיס התנועה: עיגול אייקון → שם → שורת מטא אחת →
 * סכום. HAT-1238 מוסיף את החריג *מתחת לסכום* (CardSide), כמו בפיגמה,
 * ולא כשורת סטטוס שנייה תחת השם — זה מה שדחס את הכרטיס ב-280.
 *
 * שורת המטא היא שלושה שדות עם מפריד · (לא | של נדרים): אמצעי,
 * כמה שולמו, חיוב הבא. ספירת התשלומים על הכרטיס היא "{{paid}} שולמו"
 * בלי המכנה — ב-280 אין מקום ל-84/999, והמכנה נשאר בטבלה ובמגירה.
 */

interface StandingOrderCardProps<T extends NedarimStandingOrderDtoIn> {
  row: Row<T>;
  isActive: boolean;
  onOpen: (order: T) => void;
  hasInquiries: boolean;
  lastChargeColors: Record<LastChargeStatus, string>;
  activeColors: Record<ActiveStatus, string>;
  /** "מוקפאת" rather than "לא פעילה" - only where the freeze action is on (HAT-1317). */
  frozenTerm?: boolean;
  /** The card's heading, when it is not the donor - the fund, on a screen of one donor's orders (HAT-10024). */
  title?: string;
}

const StandingOrderCard = <T extends NedarimStandingOrderDtoIn,>({
  row,
  isActive,
  onOpen,
  hasInquiries,
  lastChargeColors,
  activeColors,
  frozenTerm = false,
  title,
}: StandingOrderCardProps<T>) => {
  const { t } = useTranslation();
  const order = row.original;

  /*
   * On the bank board every card is a bank order, so the word says nothing and the row is
   * shorter without it (HAT-1135). One of the office's own credit orders on the credit board
   * says where it comes from instead of how it is paid.
   */
  const isApt = isAptOrder(order);
  const methodLabel =
    order.type === STANDING_ORDER_TYPE_CREDIT
      ? (isApt ? t('standingOrders.viaOffice') : t('standingOrders.credit'))
      : order.type === STANDING_ORDER_TYPE_BANK
        ? (isApt ? null : t('standingOrders.bank'))
        : order.type;

  const nextDate = parseDate(order.nextDate ?? null);
  const lastCharge = getLastChargeStatus(order);
  const active = getActiveStatus(order);
  const paid = getPaymentsPaid(order);

  // חריג בלבד: חיוב שנכשל גובר על "כבוי" כשלוחות התשומת-לב מוגבל לשורה אחת.
  const exception =
    lastCharge === 'failed'
      ? {
        color: lastChargeColors.failed,
        text: t(order.type === STANDING_ORDER_TYPE_BANK ? 'standingOrders.lastCharge_returned' : 'standingOrders.lastCharge_failed'),
      }
      : active === 'disabled'
        ? { color: activeColors.disabled, text: t(activeStatusKey('disabled', frozenTerm)) }
        : active === 'awaitingBank'
          ? { color: activeColors.awaitingBank, text: t('standingOrders.active_awaitingBank') }
          : active === 'cancelled'
            ? { color: activeColors.cancelled, text: t('standingOrders.active_cancelled') }
            : null;

  return (
    <ListCard isActive={isActive} onClick={() => onOpen(order)}>
      <CardIcon>
        <img src={order.type === STANDING_ORDER_TYPE_BANK ? bankIcon : paymentIcon} alt="" />
      </CardIcon>

      <CardBody>
        <CardTitle strong>{title ?? (order.clientName || t('standingOrders.donor'))}</CardTitle>
        <CardMeta
          separator=" · "
          items={[
            methodLabel,
            t('standingOrders.paidUnlimited', { paid }),
            nextDate ? `${t('standingOrders.nextChargeDate')} ${formatDateShort(nextDate)}` : null,
            /*
             * Why the office stopped it (HAT-1439), where a running order says when it charges
             * next - a stopped one has no next date. Not beside «מוקפאת» on the side: that column
             * is narrow, and a long reason there takes the width this line needs.
             */
            active === 'disabled'
              ? (order.autoFreeze ? t('standingOrders.autoFrozenReason', { count: order.autoFreeze.months }) : order.aptStopReason)
              : null,
          ]}
        />
      </CardBody>

      <CardSide>
        <CardAmount positive={!exception} muted={Boolean(exception)}>
          {formatCurrencyShort(order.amount)}
        </CardAmount>
        {exception && <CardStatusLine color={exception.color}>{exception.text}</CardStatusLine>}
      </CardSide>

      {hasInquiries && (
        <CardRowActions>
          <CardAction>
            <OpenInquiryButton resourceType="StandingOrder" resourceId={String(order.standingOrderId)} size="small" />
          </CardAction>
        </CardRowActions>
      )}
    </ListCard>
  );
};

export default StandingOrderCard;
